import { createApp } from 'vue';
import { ConfigurationService } from '@nsftx/games-config';
import * as packageTranslations from '@nsftx/i18n-keno';
import createTranslationsProxy from '@nsftx/games-sdk-js/src/utility/translationsProxy';
import eventBus from '@nsftx/games-sdk-js/src/utility/eventBus';
import sentry from '@nsftx/games-sdk-js/src/utility/sentry';
import gamesStatic from '@nsftx/games-sdk-js/src/api/gamesStatic';
import {
  toLower,
  upperFirst,
  defaultTo,
  assign,
  omit,
  isNil,
  range,
} from 'lodash';
import { pipe } from 'lodash/fp';
import App from '@/App';
import router from '@/router';
import store from '@/store';

import LocalizedLabel from '@/components/LocalizedLabel';
import { webViewIntegration } from './utility';
import i18n from './i18n';

let accountData;
let deviceData;
let companyData;
let betshopData;
let settingsData;
let productUuid = null;

window.addEventListener('message', (event) => {
  // Slave.Load message can occur before bus init
  if (event.data.action === 'Slave.Load') {
    productUuid = event.data.data.account.uuid;
  }
});

(async () => {
  const formatQueryParam = pipe(toLower, upperFirst);
  const formatProductName = (productName) =>
    `Plugin${formatQueryParam(productName)}`;

  const queryParams = new URLSearchParams(window.location.search);
  const applicationName = formatQueryParam(
    defaultTo(queryParams.get('application'), 'Web'),
  );
  /**
   * Used only to display correct layout and style, without considering any logic.
   * You should use only this prop for layout and style differences between web and terminal.
   *
   * Possible values:
   * - `"terminal"`: Renders the terminal-style layout.
   * - `"web"`: Renders the standard web layout.
   *
   * Default value: `"web"`
   * @type {"terminal" | "web"}
   */
  const layout = queryParams.get('layout') || 'web';
  const tenantId = queryParams.get('tenantId');
  const token = queryParams.get('token');
  const clientType =
    queryParams.get('authStrategy') || queryParams.get('clientType');
  const playerId =
    queryParams.get('id') ||
    queryParams.get('playerId') ||
    queryParams.get('player');
  const deployment = window.location.hostname.includes('nsoft.app')
    ? 'cf'
    : 'netlify';
  const platform = queryParams.get('platform');
  const customDomain = queryParams.get('customDomain');
  const isNextGen = platform === 'NextGen';
  const parent = queryParams.get('parent');
  const nextGenStandaloneIntegration =
    typeof parent !== 'undefined' && parent === 'false';
  let productName = queryParams.get('productName');
  const productNamePrefixes = ['SRMV', 'NVNV', 'NSNV'];
  for (const productPrefix of productNamePrefixes) {
    if (productName.startsWith(productPrefix))
      productName = productName.slice(productPrefix.length);
  }
  if (isNextGen) {
    window.addEventListener('message', (event) => {
      if (event.data.action === 'User.AuthorizationChanged') {
        const authData = event.data.data.auth;
        const loggedIn = !!authData.token;
        authData.user.logged = loggedIn;
        store.dispatch('setUserBalance', authData.user.balance);
        store.dispatch('user/updateUser', {
          auth: authData,
          profile: authData.user,
        });
      }
      if (event.data.action === 'User.BalanceChanged') {
        store.dispatch('setUserBalance', event.data.data.balance);
      }
    });

    window.addEventListener('Configuration:SetConfiguration', (event) => {
      store.dispatch('setNextGenConfig', event.detail);
    });
  }

  const getSlaveId = (path) => {
    if (path === '/ticket-history') {
      return 'PluginKenoTicketHistory';
    }
    if (path === '/results') {
      return 'PluginKenoResults';
    }

    return formatProductName(defaultTo(queryParams.get('productName'), 'Keno'));
  };

  const clientAdapters = [];

  const nextGenAdapter = {
    name: 'IgniteAdapter',
    options: {
      allowedOrigins: '*',
      displayId: 'keno',
      version: '1.1',
      providerId: process.env.VUE_APP_ENVIRONMENT === 'production' ? 30 : 26,
      environment: process.env.VUE_APP_ENVIRONMENT,
    },
  };

  const gravityGatewayAdapter = {
    name: 'GravityGatewayAdapter',
    options: {
      slaveId: getSlaveId(window.location.pathname),
      eventPropagation: {
        click: true,
        keydown: '*',
      },
    },
  };

  if (isNextGen) {
    clientAdapters.push(nextGenAdapter);
  } else {
    clientAdapters.push(gravityGatewayAdapter);
  }

  const configSettings = {
    environment: process.env.VUE_APP_ENVIRONMENT,
    applicationName,
    channel: applicationName.toLowerCase(),
    productName: productName,
    clientAdapters,
    ui: tenantId,
    deployment,
    customDomain,
  };
  const usePackageTranslations = store.state.usePackageTranslations;
  if (!usePackageTranslations)
    configSettings.translationDomains = [applicationName.toLowerCase()];
  const configService = new ConfigurationService(configSettings);

  if (applicationName === 'Terminal') {
    window.addEventListener('message', (event) => {
      const { data } = event;
      const { data: terminalData } = data;

      // eslint-disable-next-line default-case
      switch (data.action) {
        case 'Slave.Load': {
          const { terminal } = terminalData;
          const { balance } = terminal;
          const { account, betshop, company, device, user, betslip, settings } =
            terminalData;

          accountData = account;
          deviceData = device;
          betshopData = betshop;
          companyData = company;
          settingsData = settings;

          store.dispatch('user/setUserBalance', balance);
          store.dispatch('user/setTerminalUser', true);
          store.dispatch('setTerminalUserData', user);
          if (betslip.blockers.length) {
            store.dispatch('setBettingDisabled', true);
            store.dispatch('gamesBetslip/setBetslipBlockers', {
              blockers: betslip.blockers,
              type: 'add',
            });
          }
          break;
        }
        case 'Betslip.Blocked': {
          const { blockers } = terminalData;
          store.dispatch('setBettingDisabled', true);
          store.dispatch('gamesBetslip/setBetslipBlockers', {
            blockers,
            type: 'add',
          });

          break;
        }
        case 'Betslip.Unblocked': {
          const { blockers } = terminalData;
          store.dispatch('setBettingDisabled', false);
          store.dispatch('gamesBetslip/setBetslipBlockers', {
            blockers,
            type: 'remove',
          });

          break;
        }
        case 'UI.Show': {
          setTimeout(() => {
            store.dispatch('setActiveComponent', terminalData.name[0]);
          }, 200);
          break;
        }
        case 'Slave.Snooze': {
          store.dispatch('setActiveComponent', null);
          break;
        }
      }
    });
  }

  try {
    const config = await configService.getConfiguration();

    config.productNameLabel = store.getters.productNameLabel;
    config.customDomain = customDomain;
    config.locale = i18n.checkLocale(config.locale);

    const deliveryPlatform = defaultTo(
      queryParams.get('deliveryPlatform'),
      defaultTo(config.applicationName, 'Web'),
    );

    store.dispatch('setLayout', layout);
    if (layout === 'terminal') {
      store.dispatch('setActiveComponent', 'BettingArea');
    }
    await store.dispatch('setConfig', omit(config, 'state'));

    if (isNextGen) {
      await store.dispatch('updateConfigWithNextGenConfig');
      store.dispatch(
        'setNextGenStandaloneIntegration',
        nextGenStandaloneIntegration,
      );
    }

    store.dispatch('setGameState', config.state);
    store.dispatch('setDeliveryPlatform', deliveryPlatform);

    if (usePackageTranslations) {
      // NPM PACKED TRANS
      const translations = await packageTranslations.exportTranslations(
        config.locale,
        {
          companyName: config.tenantName || config.rawData?.companyInfo?.name,
          companyUuid: config.tenantId,
          env: config.environment,
        },
      );
      const proxyTranslations = createTranslationsProxy(translations, [
        'ngs.name.',
        'keno_',
      ]);
      await store.dispatch('setTranslations', proxyTranslations);
    } else {
      // STANDARD STATIC + HTTP CONFIG TRANS
      const [translations, localTranslations] = await Promise.all([
        gamesStatic.getTranslations(config.locale),
        await i18n.getTranslations(config.locale),
      ]);
      store.dispatch(
        'setTranslations',
        assign(config.translations, translations, localTranslations.default),
      );
    }

    const maxNumberOfFutureRounds = config.futureBets.value;
    const betslipFlags = config.ui?.config?.betslip;

    const isPaymentPerBetStrategy =
      config.paymentStrategy[toLower(applicationName)].value === 'perBet';
    const paymentChips = config.ui?.config?.paymentChips;

    const gamesBetslipConfig = {
      channel: deliveryPlatform,
      rules: config.rules,
      taxes: config.taxes,
      futureRounds: range(1, maxNumberOfFutureRounds + 1),
      isFuturePerTicketAllowed: maxNumberOfFutureRounds > 1,
      betLayout: betslipFlags?.betLayout ?? 'A',
      isPossibleWinActive: betslipFlags?.isPossibleWinActive ?? false,
      isPaymentBetAllowed: betslipFlags?.isPaymentBetAllowed ?? true,
      isPaymentPerBetAllowed: betslipFlags?.isPaymentPerBetAllowed ?? true,
      isFuturePerBetAllowed: betslipFlags?.isFuturePerBetAllowed ?? false,
      isTicketCancelAllowed: config.cancelEnabled ?? true,
      isTicketRebetAllowed: betslipFlags?.isTicketRebetAllowed ?? true,
      isPaymentPerBetStrategy,
      isAddButtonActive: true,
      minIncrement: betslipFlags?.minIncrement ?? 0.01,
      ticketTypes: betslipFlags?.ticketTypes ?? ['single'],
      paymentChips,
    };

    store.dispatch('gamesBetslip/setConfig', gamesBetslipConfig);
    store.dispatch('setRules', config.rules);
    store.dispatch('setDeviceLayout');
    store.state.config.productUuid = productUuid;

    if (playerId) {
      store.state.config.playerId = playerId;
    }

    if (applicationName === 'Terminal') {
      store.state.config.account = accountData;
      store.state.config.device = deviceData;
      store.state.config.betshop = betshopData;
      store.state.config.companyData = companyData;
      store.state.config.settingsData = settingsData;
    }

    if (token && config.ui.config.mode === 'standalone') {
      store.state.config.platformConfig.clientType = clientType;
      store.state.config.platformConfig.playerId = playerId;
      await store.dispatch('user/setUserSessionToken', token);
      await store.dispatch('getPlayer', { config, token });
    }
  } catch (error) {
    store.dispatch('notifications/setNotification', {
      message: 'invalid_configuration_error',
      status: 'negative',
    });
  }

  await store.dispatch('getPaytable');

  window.addEventListener('Bus:User.AuthorizationChanged', async (event) => {
    if (store.getters.isTerminalChannel) {
      store.dispatch('setTerminalUserData', event.detail.data);
      return;
    }

    const { token: userToken } = event.detail.data.auth ?? event.detail.data;
    if (!isNil(userToken)) {
      await store.dispatch('user/updateUser', {
        auth: {
          token: event.detail.data.auth.token,
          tpToken: event.detail.data.auth.tpToken,
        },
        profile: {
          ...event.detail.data.auth.user,
          logged: true,
        },
        isLoggedIn: true,
      });
      await store.dispatch(
        'user/setUserBalance',
        event.detail.data.auth.user.balance,
      );

      eventBus.$emit('Keno:AuthorizationChanged', {
        isUserLoggedIn: true,
        userUuid: store.getters.user.profile.id,
      });
    } else {
      eventBus.$emit('Keno:AuthorizationChanged', {
        isUserLoggedIn: false,
        userUuid: null,
      });

      await store.dispatch('gamesBetslip/clearBetslip');

      await store.dispatch('user/updateUser', {
        auth: {
          token: event.detail.data.auth.token,
          tpToken: event.detail.data.auth.tpToken,
        },
        profile: {
          ...event.detail.data.auth.user,
          logged: false,
        },
        isLoggedIn: false,
      });

      store.dispatch('user/setUserBalance', 0);
      store.dispatch('setIsTicketHistoryOpen', false);
    }
  });

  const app = createApp({
    extends: App,
    beforeCreate() {
      if (webViewIntegration.isActive()) {
        if (window.WebAppListener) {
          window.WebAppListener.sendMessage('Slave.Init');
        }
      }
    },
  });

  if (process.env.VUE_APP_SENTRY_ACTIVE === 'true') {
    sentry.start(app, process.env.VUE_APP_SENTRY_DSN, {
      tracesSampleRate: 0,
      ignoreErrors: [
        'Network Error',
        'timeout of 5000ms exceeded',
        /.*WakeLock.*/gm,
      ],
    });
  }
  app.config.productionTip = false;
  app.config.globalProperties.$config = configService;

  app.component('LocalizedLabel', LocalizedLabel);
  app.use(router).use(store).mount('#app');
})();
